import type { ServerEvent } from '@shared/types'

const EVENTS_URL = '/api/events'
const BASE_DELAY_MS = 1_000
const MAX_DELAY_MS = 30_000

function backoffDelay(attempt: number): number {
  const ceiling = Math.min(BASE_DELAY_MS * 2 ** (attempt - 1), MAX_DELAY_MS)
  // Half-fixed, half-jittered: many tabs coming back from one outage spread
  // themselves out instead of retrying in lockstep.
  return ceiling / 2 + Math.random() * (ceiling / 2)
}

/**
 * Owns the single SSE connection for the app.
 *
 * `EventSource` reconnects on its own, but with no backoff and no way to observe
 * failures — so the source is closed on error and reopened on our own schedule.
 */
export function connectEvents(onEvent: (e: ServerEvent) => void): () => void {
  let source: EventSource | null = null
  let timer: ReturnType<typeof setTimeout> | null = null
  let attempt = 0
  let disposed = false

  function clearTimer(): void {
    if (timer === null) return
    clearTimeout(timer)
    timer = null
  }

  function closeSource(): void {
    if (!source) return
    source.removeEventListener('message', handleMessage)
    source.removeEventListener('error', handleError)
    source.close()
    source = null
  }

  function scheduleReconnect(): void {
    if (disposed || timer !== null) return
    attempt += 1
    timer = setTimeout(() => {
      timer = null
      open()
    }, backoffDelay(attempt))
  }

  function handleMessage(event: MessageEvent<string>): void {
    let data: unknown
    try {
      data = JSON.parse(event.data)
    } catch {
      // A truncated frame must not tear down a stream that is otherwise healthy.
      return
    }
    if (typeof data !== 'object' || data === null) return

    const serverEvent = data as ServerEvent
    if (typeof serverEvent.type !== 'string') return

    // Reset on `hello`, not on `open`: a proxy can accept the connection and then
    // drop it, and that must still count as a failed attempt.
    if (serverEvent.type === 'hello') attempt = 0

    onEvent(serverEvent)
  }

  function handleError(): void {
    closeSource()
    scheduleReconnect()
  }

  function open(): void {
    if (disposed || source) return
    const next = new EventSource(EVENTS_URL)
    source = next
    next.addEventListener('message', handleMessage)
    next.addEventListener('error', handleError)
  }

  function handleVisibilityChange(): void {
    if (disposed || document.visibilityState !== 'visible') return
    // Down and waiting out a long backoff — a returning user should not.
    if (source) return
    clearTimer()
    open()
  }

  document.addEventListener('visibilitychange', handleVisibilityChange)
  open()

  return () => {
    disposed = true
    clearTimer()
    document.removeEventListener('visibilitychange', handleVisibilityChange)
    closeSource()
  }
}
